import { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useUpdatePreferences } from '../../api/hooks/useUser';
import { useToast } from '../../contexts/ToastContext';

interface ToggleRowProps {
  label: string;
  description: string;
  checked: boolean;
  disabled: boolean;
  onChange: (value: boolean) => void;
}

function ToggleRow({ label, description, checked, disabled, onChange }: ToggleRowProps) {
  return (
    <div className="flex items-start justify-between gap-4 p-4">
      <div>
        <p className="text-sm font-medium text-text-primary">{label}</p>
        <p className="text-sm text-text-secondary mt-0.5">{description}</p>
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        onClick={() => onChange(!checked)}
        disabled={disabled}
        className={`
          relative shrink-0 mt-0.5 inline-flex h-6 w-11 items-center rounded-full transition-colors
          focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 disabled:opacity-50
          ${checked ? 'bg-primary' : 'bg-border'}
        `}
      >
        <span
          className={`
            inline-block h-4 w-4 rounded-full bg-white shadow transition-transform
            ${checked ? 'translate-x-6' : 'translate-x-1'}
          `}
        />
      </button>
    </div>
  );
}

export function NotificationsTab() {
  const { user } = useAuth();
  const updatePreferences = useUpdatePreferences();
  const { showToast } = useToast();

  const [weeklyDigest, setWeeklyDigest] = useState(
    (user?.preferences?.weekly_digest as boolean | undefined) ?? true
  );
  const [marketingEmails, setMarketingEmails] = useState(
    user?.marketing_emails ?? true
  );

  const handleChange = async (key: 'weekly_digest' | 'marketing_emails', value: boolean) => {
    if (key === 'weekly_digest') setWeeklyDigest(value);
    else setMarketingEmails(value);

    try {
      await updatePreferences.mutateAsync({ [key]: value });
      showToast('Preferences saved.', 'success');
    } catch {
      // Revert on failure
      if (key === 'weekly_digest') setWeeklyDigest(!value);
      else setMarketingEmails(!value);
      showToast('Failed to save preferences.', 'error');
    }
  };

  return (
    <div className="max-w-lg">
      <h2 className="text-lg font-semibold text-text-primary mb-1">Notifications</h2>
      <p className="text-sm text-text-secondary mb-6">
        Choose which emails you receive from us.
      </p>

      <div className="border border-border rounded-xl divide-y divide-border">
        <ToggleRow
          label="Weekly invite summary"
          description="A short stats update every Monday for each of your active invites — guests, attending, declined, and new responses."
          checked={weeklyDigest}
          disabled={updatePreferences.isPending}
          onChange={(v) => handleChange('weekly_digest', v)}
        />
        <ToggleRow
          label="Product updates & offers"
          description="Occasional emails about new features, tips, and special offers. We keep it light."
          checked={marketingEmails}
          disabled={updatePreferences.isPending}
          onChange={(v) => handleChange('marketing_emails', v)}
        />
      </div>
    </div>
  );
}
